import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ChatInterface from './ChatInterface';
import ChatbotSelector from './ChatbotSelector';
import ChatbotProfile from './ChatbotProfile';
import ConversationsPage from './ConversationsPage';
import LoadingScreen from './ui/LoadingScreen';

import DeletionCountdownBanner from './DeletionCountdownBanner';
import ConversationSidebar from './ConversationSidebar';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import MobileBottomNav from './MobileBottomNav';
import LoginModal from './LoginModal';
import Footer from './Footer';
import { ChatbotPersonality } from '../types/chatbot';
import { companionService } from '../services/companionService';
// CompanionAdapter is now used in the service layer
import { chatApi, ChatSummaryItem } from '../services/chatApi';

interface ChatbotStats {
  likeCount: number;
  chatCount: number;
  isLiked: boolean;
  hasStartedChat: boolean;
}

interface DashboardProps {
  category?: string;
}

const Dashboard: React.FC<DashboardProps> = ({ category }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // State declarations first
  const [activeTab, setActiveTab] = useState<'chat' | 'select' | 'conversations' | 'characters'>('select');
  const [selectedChatbot, setSelectedChatbot] = useState<ChatbotPersonality | null>(null);
  const [showChatbotProfile, setShowChatbotProfile] = useState<ChatbotPersonality | null>(null);
  const [chatbotStats, setChatbotStats] = useState<{ [key: string]: ChatbotStats }>({});
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [showConversationSidebar, setShowConversationSidebar] = useState(false);
  const [chatInitializing, setChatInitializing] = useState(false);
  const [showAuthLoading, setShowAuthLoading] = useState(authLoading);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState<'login' | 'register'>('login');
  const [pendingProfileChatbot, setPendingProfileChatbot] = useState<ChatbotPersonality | null>(null);

  // Effects after state declarations
  // Ensure auth loading shows for at least 300ms to prevent flashing (reduced for faster auth)
  useEffect(() => {
    if (authLoading) {
      setShowAuthLoading(true);
    } else {
      const timer = setTimeout(() => {
        setShowAuthLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [authLoading]);

  // Reset chat initialization loading when switching away from chat
  useEffect(() => {
    if (activeTab !== 'chat') {
      setChatInitializing(false);
    }
  }, [activeTab]);

  // Debug logging for loading states (remove for production)
  useEffect(() => {
    console.log('🔍 Dashboard loading states:', { 
      authLoading, 
      showAuthLoading,
      chatInitializing, 
      showingLoader: showAuthLoading || chatInitializing,
      activeTab,
      hasUser: !!user 
    });
  }, [authLoading, showAuthLoading, chatInitializing, activeTab, user]);

  // Handle conversation selection from URL parameters
  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    if (conversationId && !authLoading && !showAuthLoading) {
      // Load chat + companion details, then open chat tab
      void handleSelectConversation(conversationId);
    }
  }, [searchParams, authLoading, showAuthLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Allow forcing a reset to the home (select) view via query param
  useEffect(() => {
    const reset = searchParams.get('reset');
    if (reset) {
      setSelectedChatbot(null);
      setSelectedConversationId(null);
      setActiveTab('select');
      // Clean the URL back to home
      navigate('/', { replace: true });
    }
  }, [searchParams, navigate]);

  // Close sidebar when clicking outside on mobile
  const handleOverlayClick = () => {
    if (window.innerWidth < 768) { // md breakpoint
      setSidebarExpanded(false);
    }
  };

  const handleChatbotSelect = async (chatbot: ChatbotPersonality) => {
    // Check if this chatbot already has stats initialized
    if (!chatbotStats[chatbot.id]) {
      // Set pending chatbot and load stats
      setPendingProfileChatbot(chatbot);
      const hasHistory = await checkChatHistory(chatbot.id);
      updateChatbotStats(chatbot.id, {
        likeCount: 0,
        chatCount: 0,
        isLiked: false,
        hasStartedChat: hasHistory,
      });
    } else {
      // Stats already exist, show profile immediately
      setShowChatbotProfile(chatbot);
    }
  };

  // Effect to show profile after stats are loaded
  useEffect(() => {
    if (pendingProfileChatbot && chatbotStats[pendingProfileChatbot.id]) {
      setShowChatbotProfile(pendingProfileChatbot);
      setPendingProfileChatbot(null);
    }
  }, [chatbotStats, pendingProfileChatbot]);

  const handleStartChat = (chatbot: ChatbotPersonality) => {
    setChatInitializing(true);
    setSelectedChatbot(chatbot);
    setSelectedConversationId(null);
    setShowChatbotProfile(null);
    setActiveTab('chat');
    // Reset loading state after a brief delay to allow ChatInterface to initialize
    setTimeout(() => setChatInitializing(false), 1500);
  };

  const handleBackToSelector = () => {
    setShowChatbotProfile(null);
  };

  const updateChatbotStats = (chatbotId: string, stats: Partial<ChatbotStats>) => {
    setChatbotStats(prev => {
      const currentStats = prev[chatbotId] || {
        likeCount: 0,
        chatCount: 0,
        isLiked: false,
        hasStartedChat: false,
      };
      
      return {
        ...prev,
        [chatbotId]: {
          ...currentStats,
          ...stats,
        }
      };
    });
  };

  // Function to check if a companion has existing chat history
  const checkChatHistory = async (companionId: string): Promise<boolean> => {
    try {
      if (!user) return false;
      
      const summary = await chatApi.getChatsSummary();
      return summary.chats.some(chat => chat.companion_id === companionId);
    } catch (error) {
      console.error('Error checking chat history:', error);
      return false;
    }
  };



  const handleSelectConversation = useCallback(async (chatId: string) => {
    try {
      setChatInitializing(true);
      
      // Find chat in summary to get companion info
      const summary = await chatApi.getChatsSummary();
      const item: ChatSummaryItem | undefined = summary.chats.find(c => c.chat_id === chatId);
      if (!item) {
        console.warn('Chat not found in summary');
        setChatInitializing(false);
        return;
      }

      // Optimistically set minimal chatbot so UI opens immediately
      if (item.companion_id) {
        setSelectedChatbot({
          id: item.companion_id,
          name: item.companion_name || 'Companion',
          age: 20,
          personality: '',
          description: item.title || '',
          welcomeMessage: item.companion_name ? `Hello! I'm ${item.companion_name}.` : 'Hello!',
          systemPrompt: '',
          avatar: undefined,
          avatarType: 'image',
          posterUrl: undefined,
          gallery: [],
          galleryAssets: [],
          mood: 'caring' as any,
        });
      }
      setSelectedConversationId(chatId);
      setActiveTab('chat');

      if (!item.companion_id) {
        console.warn('Selected chat has no companion associated');
        return;
      }

      // Enrich with full companion details using the new service method
      const chatbot = await companionService.getCompanionByIdChatbot(item.companion_id);

      setSelectedChatbot(chatbot);
      
      // Update stats to reflect that this companion has chat history
      updateChatbotStats(chatbot.id, {
        hasStartedChat: true,
      });
      
      // Reset loading state after companion data is loaded
      setTimeout(() => setChatInitializing(false), 1000);
    } catch (error) {
      console.error('Failed to open chat:', error);
      setChatInitializing(false);
    }
  }, []);

  const handleNewChat = () => {
    setSelectedChatbot(null);
    setSelectedConversationId(null);
    setChatInitializing(false);
    setActiveTab('select');
  };

  return (
    <div className="min-h-screen bg-dark-900 flex flex-col">
      <DeletionCountdownBanner 
        onCancelSuccess={() => {
          window.location.reload();
        }}
      />

      {/* Mobile Sidebar Overlay */}
      {sidebarExpanded && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={handleOverlayClick}
        />
      )}

      {/* Main Layout Container */}
      <div className="flex flex-1">
        {/* Left Sidebar */}
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          sidebarExpanded={sidebarExpanded}
          setSidebarExpanded={setSidebarExpanded}
          onShowLogin={(mode = 'login') => {
            setLoginModalMode(mode);
            setShowLoginModal(true);
          }}
        />

        {/* Top Bar */}
        <TopBar 
          sidebarExpanded={sidebarExpanded} 
          setSidebarExpanded={setSidebarExpanded}
          onShowLogin={(mode = 'login') => {
            setLoginModalMode(mode);
            setShowLoginModal(true);
          }}
        />

        {/* Main Content */}
        <div className={`flex-1 transition-all duration-500 ease-in-out ${sidebarExpanded ? 'lg:ml-72' : 'lg:ml-20'} ml-0 pt-30 lg:pt-16 pb-24 lg:pb-0 flex flex-col`}>
          <div className="flex-1 p-4 sm:p-6 pt-8 lg:pt-6">
            {/* Content - Hide completely during loading */}
            {!(showAuthLoading || chatInitializing) && (
              <div className="max-w-7xl mx-auto min-h-full flex items-center justify-center lg:block">
              {activeTab === 'chat' && selectedChatbot && user && (
                <div className="h-[calc(100vh-8rem)] lg:h-[calc(100vh-4rem)] flex flex-col">
                  {/* Top Navigation Bar (similar to CompanionChat) */}
                  <div className="h-14 bg-dark-800 border-b border-dark-600 flex items-center justify-between px-4 flex-shrink-0 rounded-t-lg">
                    {/* Left side - Back button and companion info */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setActiveTab('conversations')}
                        className="p-2 text-dark-300 hover:text-white hover:bg-dark-700 rounded-lg transition-all duration-300"
                        title="Back to conversations"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      
                      <div className="flex items-center gap-2">
                        {selectedChatbot.avatar && (
                          <img 
                            src={selectedChatbot.avatar} 
                            alt={selectedChatbot.name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <h2 className="text-white font-semibold">{selectedChatbot.name}</h2>
                          <p className="text-dark-300 text-xs">Online</p>
                        </div>
                      </div>
                    </div>

                    {/* Right side - Profile toggle */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowConversationSidebar(!showConversationSidebar)}
                        className={`p-2 rounded-lg transition-all duration-300 ${
                          showConversationSidebar 
                            ? 'text-accent-purple bg-accent-purple/20' 
                            : 'text-dark-300 hover:text-white hover:bg-dark-700'
                        }`}
                        title="Toggle profile"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Main Content Area */}
                  <div className="flex-1 flex overflow-hidden gap-4 p-4 pt-6">
                    {/* Chat Area */}
                    <div className="flex-1 flex flex-col">
                      <ChatInterface
                        selectedChatbot={selectedChatbot}
                        conversationId={selectedConversationId}
                        onToggleProfile={() => setShowConversationSidebar(!showConversationSidebar)}
                      />
                    </div>

                    {/* Right Sidebar - AI Profile */}
                    <div className={`w-80 flex-shrink-0 transition-all duration-300 transform ${
                      showConversationSidebar ? 'translate-x-0' : 'translate-x-full'
                    }`}>
                      {selectedChatbot && (
                        <ConversationSidebar
                          chatbot={selectedChatbot}
                          chatbotStats={chatbotStats[selectedChatbot.id] || {
                            likeCount: 0,
                            chatCount: 0,
                            isLiked: false,
                            hasStartedChat: true,
                          }}
                          onUpdateStats={(stats) => updateChatbotStats(selectedChatbot.id, stats)}
                          onClose={() => setShowConversationSidebar(false)}
                          onShowProfile={() => setShowChatbotProfile(selectedChatbot)}
                        />
                      )}
                    </div>
                  </div>

                  {/* Profile Overlay for mobile */}
                  {showConversationSidebar && (
                    <div 
                      className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                      onClick={() => setShowConversationSidebar(false)}
                    />
                  )}
                </div>
              )}

              {activeTab === 'chat' && !user && (
                <div className="text-center max-w-md">
                  <div className="text-6xl mb-6">🔒</div>
                  <h1 className="text-3xl font-bold text-white mb-4">Sign In Required</h1>
                  <p className="text-dark-300 mb-8">You need to sign in to chat with AI companions.</p>
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        setLoginModalMode('login');
                        setShowLoginModal(true);
                      }}
                      className="w-full px-6 py-3 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300"
                    >
                      Sign In
                    </button>
                    <button
                      onClick={() => setActiveTab('select')}
                      className="w-full px-6 py-3 bg-dark-700 text-white rounded-xl font-medium hover:bg-dark-600 transition-all duration-300"
                    >
                      Back to Home
                    </button>
                  </div>
                </div>
              )}
              
              {activeTab === 'select' && (
                <>
                  <ChatbotSelector
                    onSelect={handleChatbotSelect}
                    chatbotStats={chatbotStats}
                    category={category}
                  />
                  
                  {showChatbotProfile && (
                    <ChatbotProfile
                      chatbot={showChatbotProfile}
                      onStartChat={handleStartChat}
                      onBack={handleBackToSelector}
                      chatbotStats={chatbotStats[showChatbotProfile.id]}
                      onUpdateStats={(stats: Partial<ChatbotStats>) => updateChatbotStats(showChatbotProfile.id, stats)}
                    />
                  )}
                </>
              )}
              
              {activeTab === 'conversations' && (
                <ConversationsPage
                  onSelectConversation={handleSelectConversation}
                  onNewChat={handleNewChat}
                />
              )}
              
              </div>
            )}

            {/* Loading Screen - Show during auth loading or chat initialization */}
            {(showAuthLoading || chatInitializing) && (
              <div className="fixed inset-0 bg-dark-900 z-[80] flex items-center justify-center" style={{ 
                position: 'fixed', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                width: '100vw', 
                height: '100vh' 
              }}>
                <LoadingScreen
                  title={showAuthLoading ? "Loading Dashboard" : "Initializing Chat"}
                  message={showAuthLoading ? "Verifying your account..." : "Setting up your conversation..."}
                />
              </div>
            )}

          </div>
          
          {/* Footer - Show on desktop only */}
          <div className="hidden lg:block">
            <Footer />
          </div>
        </div>
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav 
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      
      {/* Login Modal - Rendered at top level for proper centering */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
        initialMode={loginModalMode}
      />
    </div>
  );
};

export default Dashboard;